import React, { memo } from 'react';
import COMMON from 'constants/common';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getClassificationName } from 'utils/common.helper';
import { SelectListType } from 'types/support/supportType';
import CLSFCN from 'constants/classification';
import SelectButtonWithLabel from 'components/system/atoms/buttons/SelectButtonWithLabel';
import { GROUP_LED_INPUT_NAMES } from 'pages/group/LedGroupCreate';

/* ********** Constants ********** */

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  rightArea: {
    display: 'flex',
    flexDirection: 'column',
  },
  settings: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '5px 0px 0 4px',
  },
  ledSetting: {
    // 設定内容の表示・非表示で要素の高さが変わらないよう、.warningTextの高さと揃える
    height: '36px',
    marginTop: '12px',
  },
  basicSettings: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    backgroundColor: '#F6F6F6',
    border: '1px solid #C8C8C8',
    padding: '5px 4px 5px 6px',
    marginTop: '12px',
  },
  keepEcoMode: {
    height: 64,
  },
  frameWhite: {
    height: 64,
  },
  pullDownEmpty: {
    height: 108,
  },
  pullDownEmptyGrand: {
    height: 27,
  },
  pullDownEmptyMulti: {
    height: 15,
  },
  pullDown: {
    width: 296,
    justifyContent: 'space-between',
    '& button': {
      width: 200,
      height: 36,
      fontSize: 19,
      border: '2px solid #000',
      borderRadius: 8,
      padding: '0 8px',
      '& div': {
        textAlign: 'left',
      },
    },
  },
  baseFontSize: {
    fontSize: 19,
  },
  label: {
    fontSize: '19px',
    color: '#0D0D0D',
    margin: 0,
  },
  warningText: {
    height: '80px',
    fontSize: '13px',
    lineHeight: '15px',
    color: '#ff0000',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    marginTop: '-22px',
    marginBottom: '-10px',
    paddingBottom: '10px',
  },
  indent: {
    display: 'block', // 改行を有効にする
    marginLeft: '20px', // 必要に応じて文字下げの幅を調整
  },
  disableInputArea: {
    position: 'absolute',
    height: '100%',
    width: '200px',
    background: 'rgb(128 128 128 / 50%)',
    borderRadius: '8px 8px 8px 8px',
    zIndex: 100,
    top: '0px',
    left: '97px',
  },
}));

/* ************ Type ************ */
type Props = {
  selectList: SelectListType;
  inputValue: {
    setting: string;
    settingDuo: string;
    directing: string;
    ecoMode: string;
    keepEcoType: string;
    communicationConf: string;
    bgm: string;
    se: string;
    voice: string;
    duoLedCustomFlg: string;
    frameWhite: string;
  };
  handleClickSelectList: (ev: React.MouseEvent<HTMLButtonElement>, id: string, settingTab?: string) => void;
  settingTab: string;
};

/* ************ Component ************ */
const LedGroupBasicSettings: React.FC<React.PropsWithChildren<Props>> = ({
  selectList,
  inputValue,
  handleClickSelectList,
  settingTab,
}) => {
  const { classes } = useStyles();
  const INPUT_NAMES = GROUP_LED_INPUT_NAMES;

  //「演出内容」の"getClassificationName"のcategoryパラメータにセットするコードのMap
  const CATEGORY_MAP: Record<string, string> = {
    [COMMON.SETTING_TAB.MULTI]: CLSFCN.DIRECTING_CONTENT.CATEGORY_CD,
    [COMMON.SETTING_TAB.DUO]: CLSFCN.LED_DIRECTING_DUO.CATEGORY_CD,
    [COMMON.SETTING_TAB.CLENA3]: CLSFCN.LED_DIRECTING.CATEGORY_CD,
    [COMMON.SETTING_TAB.JACK]: CLSFCN.LED_DIRECTING.CATEGORY_CD,
    [COMMON.SETTING_TAB.GRAND]: CLSFCN.LED_DIRECTING.CATEGORY_CD,
  };

  return (
    <Box className={classes.rightArea}>
      <Box className={classes.settings}>
        {settingTab === COMMON.SETTING_TAB.MULTI && (
          <Box className={classes.warningText}>
            <div>※演出内容㉒レッド～㉖ホワイトはクレナマルチと</div>
            <div>{'　'}クレナグランデュオ専用の演出となります。</div>
          </Box>
        )}
        {settingTab === COMMON.SETTING_TAB.DUO && (
          <SelectButtonWithLabel
            className={`${classes.pullDown} ${classes.ledSetting}`}
            label="設定内容"
            onClick={(ev) => handleClickSelectList(ev, INPUT_NAMES.SETTING_DUO)}
            arrowSize="28px"
            isSelected={selectList.name === INPUT_NAMES.SETTING_DUO}>
            {getClassificationName(CLSFCN.LED_SETTING.CATEGORY_CD, inputValue.settingDuo)}
          </SelectButtonWithLabel>
        )}
        {[COMMON.SETTING_TAB.CLENA3, COMMON.SETTING_TAB.JACK, COMMON.SETTING_TAB.GRAND].includes(settingTab) && (
          <SelectButtonWithLabel
            className={`${classes.pullDown} ${classes.ledSetting}`}
            label="設定内容"
            onClick={(ev) => handleClickSelectList(ev, INPUT_NAMES.SETTING)}
            arrowSize="28px"
            isSelected={selectList.name === INPUT_NAMES.SETTING}>
            {getClassificationName(CLSFCN.LED_SETTING.CATEGORY_CD, inputValue.setting)}
          </SelectButtonWithLabel>
        )}
        <Box sx={{ position: 'relative' }}>
          <SelectButtonWithLabel
            className={classes.pullDown}
            label="演出内容"
            onClick={(ev) => handleClickSelectList(ev, INPUT_NAMES.DIRECTING)}
            arrowSize="28px"
            isSelected={selectList.name === INPUT_NAMES.DIRECTING}>
            {
              /* 選択中のタブごとに「演出内容」にセットする名称をCATEGORY_MAPから取り出してgetClassificationNameのパラメータにセットする */
              getClassificationName(CATEGORY_MAP[settingTab], inputValue.directing)
            }
          </SelectButtonWithLabel>
          {inputValue.settingDuo === CLSFCN.LED_SETTING.CUSTOM && <Box className={classes.disableInputArea} />}
        </Box>
        {settingTab === COMMON.SETTING_TAB.CLENA3 ? (
          <SelectButtonWithLabel
            className={classes.pullDown}
            label="エコモード"
            onClick={(ev) => handleClickSelectList(ev, INPUT_NAMES.ECO_MODE, settingTab)}
            arrowSize="28px"
            isSelected={selectList.name === INPUT_NAMES.ECO_MODE}>
            {getClassificationName(CLSFCN.LED_ECO_MODE.CATEGORY_CD, inputValue.ecoMode)}
          </SelectButtonWithLabel>
        ) : (
          <SelectButtonWithLabel
            className={classes.pullDown}
            label="エコモード"
            onClick={(ev) => handleClickSelectList(ev, INPUT_NAMES.ECO_MODE, settingTab)}
            arrowSize="28px"
            isSelected={selectList.name === INPUT_NAMES.ECO_MODE}>
            {settingTab === COMMON.SETTING_TAB.DUO
              ? getClassificationName(CLSFCN.LED_ECO_TYPE_DUO.CATEGORY_CD, inputValue.ecoMode)
              : getClassificationName(CLSFCN.LED_ECO_TYPE.CATEGORY_CD, inputValue.ecoMode)}
          </SelectButtonWithLabel>
        )}

        {[COMMON.SETTING_TAB.GRAND, COMMON.SETTING_TAB.MULTI, COMMON.SETTING_TAB.DUO].includes(settingTab) ? (
          <Box className={classes.keepEcoMode}>
            <Box className={classes.label}>プレイ時もエコモード継続</Box>
            <SelectButtonWithLabel
              className={classes.pullDown}
              label=" "
              onClick={(ev) => handleClickSelectList(ev, INPUT_NAMES.KEEP_ECO_TYPE, settingTab)}
              arrowSize="28px"
              isSelected={selectList.name === INPUT_NAMES.KEEP_ECO_TYPE}>
              {getClassificationName(CLSFCN.LED_ECO_MODE.CATEGORY_CD, inputValue.keepEcoType)}
            </SelectButtonWithLabel>
          </Box>
        ) : (
          // 非表示時に他の要素の位置が変わらないよう、余白を描画する
          <Box className={classes.keepEcoMode} />
        )}

        {[COMMON.SETTING_TAB.DUO].includes(settingTab) ? (
          <Box className={classes.frameWhite}>
            <Box className={classes.label}>フレームホワイト</Box>
            <SelectButtonWithLabel
              className={classes.pullDown}
              label=" "
              onClick={(ev) => handleClickSelectList(ev, INPUT_NAMES.FRAME_WHITE, settingTab)}
              arrowSize="28px"
              isSelected={selectList.name === INPUT_NAMES.FRAME_WHITE}>
              {getClassificationName(CLSFCN.FRAME_WHITE.CATEGORY_CD, inputValue.frameWhite)}
            </SelectButtonWithLabel>
          </Box>
        ) : (
          // 非表示時に他の要素の位置が変わらないよう、余白を描画する
          <Box className={classes.frameWhite} />
        )}
      </Box>
      <Box className={classes.basicSettings}>
        <SelectButtonWithLabel
          className={classes.pullDown}
          label="通信設定"
          onClick={(ev) => handleClickSelectList(ev, INPUT_NAMES.COMMUNICATION_CONF)}
          arrowSize="28px"
          isSelected={selectList.name === INPUT_NAMES.COMMUNICATION_CONF}>
          {getClassificationName(CLSFCN.GROUP_COMMUNICATION_CONF.CATEGORY_CD, inputValue.communicationConf)}
        </SelectButtonWithLabel>
        <SelectButtonWithLabel
          className={classes.pullDown}
          label="BGM"
          onClick={(ev) => handleClickSelectList(ev, INPUT_NAMES.BGM)}
          arrowSize="28px"
          isSelected={selectList.name === INPUT_NAMES.BGM}>
          {getClassificationName(CLSFCN.GROUP_BGM.CATEGORY_CD, inputValue.bgm)}
        </SelectButtonWithLabel>
      </Box>
    </Box>
  );
};

export default memo(LedGroupBasicSettings);
